import { useEffect, useState, useCallback } from 'react'
import { Megaphone, Send, Save, Copy, Trash2, Power, BarChart3, Users } from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import SearchableSelect from '../components/SearchableSelect'
import { successModal, errorModal, confirmModal } from '../components/ui'

const CANAIS = [['app', 'Plataforma/App'], ['email', 'E-mail'], ['whatsapp', 'WhatsApp']]
const CATEGORIAS = [['personalizada', 'Comunicado'], ['financeiro', 'Financeiro'], ['rede', 'Rede'], ['loja', 'Loja'], ['carreira', 'Carreira'], ['sistema', 'Sistema']]
const DESTINOS = [
  ['todos_licenciados', 'Todos os Licenciados'], ['todos_clientes', 'Todos os Clientes'],
  ['licenciado', 'Um Licenciado'], ['estado', 'Por Estado'], ['cidade', 'Por Cidade'],
  ['status_carreira', 'Por Status de Carreira'], ['patrocinador', 'Por Patrocinador (diretos)'],
  ['linha', 'Por Linha Descendente'], ['produto', 'Por Produto'],
]
const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400'
const fmt = (v) => (v ? new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-')

function Card({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-neutral-200">
      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-2"><Icon size={16} className="text-white" /></div>
      <p className="text-2xl font-heading font-extrabold text-graphite">{value ?? '-'}</p>
      <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mt-0.5">{label}</p>
    </div>
  )
}

export default function CentralNotificacoes() {
  const [form, setForm] = useState({ titulo: '', subtitulo: '', mensagem: '', link: '', categoria: 'personalizada', prioridade: 'normal', canais: ['app'], destino_tipo: 'todos_licenciados', filtro: {} })
  const [lics, setLics] = useState([])
  const [ofs, setOfs] = useState([])
  const [rows, setRows] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/central-notificacoes').then((r) => setRows(r.data.data || [])).catch(() => {}).finally(() => setLoading(false))
    api.get('/central-notificacoes/dashboard').then((r) => setStats(r.data)).catch(() => {})
  }, [])
  useEffect(() => { load() }, [load])
  useEffect(() => {
    api.get('/licenciados?limit=300').then((r) => setLics(r.data.data || [])).catch(() => {})
    api.get('/ofertas/publico').then((r) => setOfs(r.data || [])).catch(() => {})
  }, [])

  const toggleCanal = (c) => setForm((f) => ({ ...f, canais: f.canais.includes(c) ? f.canais.filter((x) => x !== c) : [...f.canais, c] }))
  const licOpts = lics.map((l) => ({ value: l.id, label: l.nome }))

  function payload(extra = {}) {
    return { titulo: form.titulo, subtitulo: form.subtitulo, mensagem: form.mensagem, link: form.link, categoria: form.categoria,
      prioridade: form.prioridade, canais: form.canais, destino_tipo: form.destino_tipo, destino_filtro: form.filtro, ...extra }
  }
  async function enviar() {
    if (!form.titulo.trim()) return errorModal('Informe o título.')
    if (!form.canais.length) return errorModal('Selecione ao menos um canal.')
    if (!(await confirmModal('Enviar esta notificação agora para os destinatários selecionados?', { title: 'Enviar notificação', okLabel: 'Enviar' }))) return
    setBusy(true)
    try {
      const { data } = await api.post('/central-notificacoes?enviar=true', payload())
      const r = data.resultado || {}
      await successModal(`Enviada para ${r.enviados || 0} destinatário(s). Push: ${r.push_ok || 0} · E-mail: ${r.email_ok || 0} · WhatsApp: ${r.whatsapp_ok || 0}.`, { title: 'Notificação enviada' })
      setForm((f) => ({ ...f, titulo: '', subtitulo: '', mensagem: '', link: '' })); load()
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao enviar') } finally { setBusy(false) }
  }
  async function salvarModelo() {
    if (!form.titulo.trim()) return errorModal('Informe o título.')
    setBusy(true)
    try { await api.post('/central-notificacoes', payload({ template: true })); await successModal('Modelo salvo.'); load() }
    catch (e) { errorModal(e?.response?.data?.detail || 'Erro') } finally { setBusy(false) }
  }
  const duplicar = (id) => api.post(`/central-notificacoes/${id}/duplicar`).then(load).catch(() => {})
  const reenviar = (id) => api.post(`/central-notificacoes/${id}/enviar`).then((r) => { successModal(`Reenviada (${r.data.resultado?.enviados || 0}).`); load() }).catch((e) => errorModal(e?.response?.data?.detail || 'Erro'))
  const excluir = async (id) => { if (await confirmModal('Excluir esta notificação?', { title: 'Excluir', okLabel: 'Excluir' })) api.delete(`/central-notificacoes/${id}`).then(load) }

  const precisaFiltro = ['licenciado', 'estado', 'cidade', 'status_carreira', 'patrocinador', 'linha', 'produto'].includes(form.destino_tipo)

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-extrabold text-graphite flex items-center gap-2"><Megaphone size={24} className="text-accent-500" /> Central de Notificações</h1>
        <p className="text-sm text-neutral-500 mt-1">Crie e envie comunicações segmentadas por perfil, região, status ou produto</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Enviados" value={stats?.enviados} icon={Send} />
        <Card label="Push entregues" value={stats?.push_ok} icon={Users} />
        <Card label="Taxa de leitura" value={stats ? `${stats.taxa_leitura}%` : '-'} icon={BarChart3} />
        <Card label="Agendadas" value={stats?.agendadas} icon={Power} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Criar */}
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-5 space-y-3">
          <p className="font-heading font-bold text-graphite">Nova notificação</p>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Título *</label><input className={inp} value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Subtítulo</label><input className={inp} value={form.subtitulo} onChange={(e) => setForm({ ...form, subtitulo: e.target.value })} /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Mensagem</label><textarea rows={3} className={inp} value={form.mensagem} onChange={(e) => setForm({ ...form, mensagem: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Categoria</label>
              <select className={inp} value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>{CATEGORIAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Prioridade</label>
              <select className={inp} value={form.prioridade} onChange={(e) => setForm({ ...form, prioridade: e.target.value })}><option value="baixa">Baixa</option><option value="normal">Normal</option><option value="alta">Alta</option></select></div>
          </div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Link (opcional)</label><input className={inp} value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="/registros/fila-analise ou https://..." /></div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Canais</label>
            <div className="flex gap-2 flex-wrap">
              {CANAIS.map(([v, l]) => (
                <button key={v} onClick={() => toggleCanal(v)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${form.canais.includes(v) ? 'bg-accent-500 text-white border-accent-500' : 'border-gray-200 text-gray-600'}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Destinatários</label>
              <select className={inp} value={form.destino_tipo} onChange={(e) => setForm({ ...form, destino_tipo: e.target.value, filtro: {} })}>{DESTINOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            {precisaFiltro && (
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Filtro</label>
                {['licenciado', 'patrocinador', 'linha'].includes(form.destino_tipo) ? (
                  <SearchableSelect value={form.filtro.id || ''} onChange={(v) => setForm({ ...form, filtro: { id: v } })} options={licOpts} placeholder="Licenciado..." />
                ) : form.destino_tipo === 'produto' ? (
                  <SearchableSelect value={form.filtro.oferta_id || ''} onChange={(v) => setForm({ ...form, filtro: { oferta_id: v } })} options={ofs.map((o) => ({ value: o.id, label: o.nome }))} placeholder="Produto..." />
                ) : (
                  <input className={inp} value={form.filtro.estado || form.filtro.cidade || form.filtro.valor || ''}
                    onChange={(e) => { const k = form.destino_tipo === 'estado' ? 'estado' : form.destino_tipo === 'cidade' ? 'cidade' : 'valor'; setForm({ ...form, filtro: { [k]: e.target.value } }) }}
                    placeholder={form.destino_tipo === 'estado' ? 'UF (ex.: GO)' : form.destino_tipo === 'cidade' ? 'Cidade' : 'Status de carreira'} />
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={enviar} disabled={busy} className="flex-1 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 text-sm font-bold shadow-md flex items-center justify-center gap-1.5 disabled:opacity-50"><Send size={15} /> Enviar agora</button>
            <button onClick={salvarModelo} disabled={busy} className="rounded-xl border border-gray-200 text-gray-600 px-4 py-2 text-sm font-semibold flex items-center gap-1.5"><Save size={15} /> Modelo</button>
          </div>
        </div>

        {/* Histórico */}
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-5">
          <p className="font-heading font-bold text-graphite mb-3">Enviadas / Modelos</p>
          {loading ? <Loader text="Carregando..." /> : rows.length === 0 ? <p className="text-sm text-neutral-400">Nenhuma notificação ainda.</p> : (
            <div className="space-y-2 max-h-[520px] overflow-y-auto tc-scroll">
              {rows.map((c) => (
                <div key={c.id} className="border border-neutral-100 rounded-xl p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-graphite truncate">{c.titulo} {c.template && <span className="text-[10px] bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded">MODELO</span>}</p>
                      <p className="text-[11px] text-neutral-400">{(c.canais || []).join(', ')} · {c.destino_tipo} · {c.enviada_em ? fmt(c.enviada_em) : 'não enviada'}</p>
                      {!!c.enviados && <p className="text-[11px] text-neutral-500 mt-0.5">Enviados {c.enviados} · push {c.push_ok} · email {c.email_ok} · zap {c.whatsapp_ok}{c.falhas ? ` · falhas ${c.falhas}` : ''}</p>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => reenviar(c.id)} title="Enviar" className="p-1.5 rounded-lg hover:bg-accent-50 text-accent-600"><Send size={14} /></button>
                      <button onClick={() => duplicar(c.id)} title="Duplicar" className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-500"><Copy size={14} /></button>
                      <button onClick={() => excluir(c.id)} title="Excluir" className="p-1.5 rounded-lg hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
